import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  CalendarCheck, 
  Phone, 
  MapPin, 
  InstagramLogo, 
  Calendar,
  Scissors,
  Chair,
  House,
  X,
  ShareNetwork,
  Plus
} from "@phosphor-icons/react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f92832a4-f773-4854-853a-afbb4cec93dc/artifacts/hfvgmn1m_JS%20Blond%20Logo%20white.png";

const AppPage = () => {
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
    }
  };

  const quickActions = [
    {
      icon: CalendarCheck,
      label: "Termin Buchen",
      color: "#D4AF37",
      url: "https://www.phorest.com/salon/jsblondsalonughaftungsbeschrae",
      external: true,
    },
    {
      icon: Phone,
      label: "Anrufen",
      color: "#9A8C73",
      url: "tel:+49",
      external: true,
    },
    {
      icon: MapPin,
      label: "Route",
      color: "#2C2C2C",
      url: "https://maps.google.com/?q=JS+Blond+Salon+Leingarten",
      external: true,
    },
    {
      icon: InstagramLogo,
      label: "@jsblondsalon",
      color: "#E1306C",
      url: "https://www.instagram.com/jsblondsalon",
      external: true,
    },
  ];

  const menuItems = [
    {
      icon: Scissors,
      title: "Services",
      description: "Balayage, Colorationen & mehr",
      url: "/#services",
    },
    {
      icon: Chair,
      title: "Co-Working",
      description: "Stuhlmiete für Friseur/innen",
      url: "/#coworking",
    },
    {
      icon: Calendar,
      title: "Events",
      description: "Workshops & Education",
      url: "/#events",
    },
    {
      icon: House,
      title: "Zur Website",
      description: "Vollständige Website ansehen",
      url: "/",
    },
  ];

  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="px-6 py-4 flex items-center justify-between">
          <img 
            src={LOGO_URL} 
            alt="JS Blond Salon" 
            className="h-8 w-auto"
            style={{ filter: "invert(1)" }}
          />
          <a 
            href="/"
            data-testid="app-home-link"
            className="w-10 h-10 rounded-full bg-[#F2EFE9] flex items-center justify-center"
          >
            <House size={20} className="text-[#2C2C2C]" />
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-6 py-8 pb-32">
        {/* Hero Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl overflow-hidden shadow-lg mb-8"
        >
          <div className="relative h-48">
            <img
              src="https://static.wixstatic.com/media/5071cb_20261aee77c64b48a57ada51485234e4~mv2.jpeg/v1/fill/w_800,h_400,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/5071cb_20261aee77c64b48a57ada51485234e4~mv2.jpeg"
              alt="JS Blond Salon"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
              <p className="text-white/80 text-sm">Willkommen bei</p>
              <h2 
                className="text-2xl text-white font-medium"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                JS Blond Salon
              </h2>
            </div>
          </div>
          <div className="p-4">
            <p className="text-sm text-[#2C2C2C]/70">
              Blondexpertise & Co-Working in Leingarten. 
              Premium Blond, gepflegte Ergebnisse und ein klarer Ablauf.
            </p>
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-4 gap-3 mb-8"
        >
          {quickActions.map((action, index) => (
            <motion.a
              key={index}
              href={action.url}
              target={action.external ? "_blank" : undefined}
              rel={action.external ? "noopener noreferrer" : undefined}
              data-testid={`quick-action-${index}`}
              className="flex flex-col items-center"
              whileTap={{ scale: 0.95 }}
            >
              <div 
                className="w-14 h-14 rounded-2xl flex items-center justify-center mb-2 shadow-md"
                style={{ backgroundColor: action.color }}
              >
                <action.icon size={24} className="text-white" weight="fill" />
              </div>
              <span className="text-xs text-center text-[#2C2C2C]/70 leading-tight">
                {action.label}
              </span>
            </motion.a>
          ))}
        </motion.div>

        {/* Menu Items */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-3"
        >
          <h3 className="text-sm font-semibold text-[#9A8C73] uppercase tracking-wider mb-4">
            Entdecken
          </h3>
          {menuItems.map((item, index) => (
            <motion.a
              key={index}
              href={item.url}
              data-testid={`menu-item-${index}`}
              className="flex items-center gap-4 bg-white p-4 rounded-xl shadow-sm"
              whileTap={{ scale: 0.98 }}
            >
              <div className="w-12 h-12 rounded-xl bg-[#F2EFE9] flex items-center justify-center">
                <item.icon size={24} className="text-[#9A8C73]" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-[#2C2C2C]">{item.title}</h4>
                <p className="text-sm text-[#2C2C2C]/60">{item.description}</p>
              </div>
            </motion.a>
          ))}
        </motion.div>

        {/* Instagram Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8"
        >
          <h3 className="text-sm font-semibold text-[#9A8C73] uppercase tracking-wider mb-4">
            Folge uns
          </h3>
          <div className="flex gap-3">
            <a
              href="https://www.instagram.com/jsblondsalon"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="app-instagram-salon"
              className="flex-1 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 p-4 rounded-xl text-white"
            >
              <InstagramLogo size={24} weight="fill" className="mb-2" />
              <p className="font-medium">@jsblondsalon</p>
              <p className="text-xs opacity-80">Salon</p>
            </a>
            <a
              href="https://www.instagram.com/jennifervallentin"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="app-instagram-jennifer"
              className="flex-1 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 p-4 rounded-xl text-white"
            >
              <InstagramLogo size={24} weight="fill" className="mb-2" />
              <p className="font-medium text-sm">@jennifervallentin</p>
              <p className="text-xs opacity-80">Jennifer</p>
            </a>
          </div>
        </motion.div>
      </main>

      {/* Install Banner */}
      <AnimatePresence>
        {showInstallPrompt && !isInstalled && (
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            className="fixed bottom-20 left-4 right-4 bg-white rounded-2xl shadow-xl p-4 z-50 border border-[#E6E2D6]"
          >
            <button
              onClick={() => setShowInstallPrompt(false)}
              className="absolute top-3 right-3 text-[#2C2C2C]/40"
            >
              <X size={20} />
            </button>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-[#9A8C73] flex items-center justify-center text-white font-bold text-lg">
                JS
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-[#2C2C2C]">App installieren</h4>
                <p className="text-sm text-[#2C2C2C]/60">Schneller Zugriff auf alle Funktionen</p>
              </div>
            </div>
            <button
              onClick={handleInstall}
              data-testid="install-app-btn"
              className="w-full mt-4 bg-[#2C2C2C] text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2"
            >
              <Plus size={18} />
              Zum Homescreen hinzufügen
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E6E2D6] p-4 z-40">
        <motion.a
          href="https://www.phorest.com/salon/jsblondsalonughaftungsbeschrae"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="app-book-btn"
          className="block w-full bg-[#D4AF37] text-white py-4 rounded-xl font-bold text-center uppercase tracking-wider"
          whileTap={{ scale: 0.98 }}
        >
          Jetzt Termin Buchen
        </motion.a>
      </div>
    </div>
  );
};

export default AppPage;
